import { Link } from "@inertiajs/react";
import { BookOpen, CheckCircle, Clock, FileText, Loader, XCircle } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface PackageSummary {
  id: number;
  candidate_id: number;
  candidate_name: string;
  candidate_firm: string;
  status: string;
  updated_at: string;
}

interface Props {
  packages: PackageSummary[];
  ai_assist: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; icon: typeof Clock; className: string }> = {
  not_started:      { label: "Not Started",       icon: Clock,        className: "text-gray-400" },
  generating:       { label: "Generating",         icon: Loader,       className: "text-blue-500" },
  ready_for_review: { label: "Ready for Review",   icon: FileText,     className: "text-yellow-600" },
  approved:         { label: "Approved",           icon: CheckCircle,  className: "text-green-500" },
  executing:        { label: "Executing",          icon: Loader,       className: "text-purple-500" },
  completed:        { label: "Completed",          icon: CheckCircle,  className: "text-green-700" },
  failed:           { label: "Failed",             icon: XCircle,      className: "text-red-500" },
};

export default function PackagesIndex({ packages }: Props) {
  return (
    <AppLayout title="Packages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Packages</h1>
          <p className="text-sm text-gray-500 mt-0.5">{packages.length} submission packages</p>
        </div>
        <Link href="/candidates" className="text-sm bg-green-700 text-white px-3 py-1.5 rounded-md hover:bg-green-800 transition-colors">
          + From Candidate
        </Link>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {packages.length === 0 ? (
          <div className="py-16 text-center">
            <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No packages yet. Open a candidate profile to create one.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Candidate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Firm</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {packages.map((pkg) => {
                const config = STATUS_CONFIG[pkg.status] ?? STATUS_CONFIG.not_started;
                const Icon = config.icon;
                return (
                  <tr key={pkg.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link href={`/candidates/${pkg.candidate_id}/package`} className="text-sm font-medium text-gray-900 hover:text-green-700">
                        {pkg.candidate_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{pkg.candidate_firm || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${config.className}`}>
                        <Icon className="w-3 h-3" /> {config.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-400">
                      {new Date(pkg.updated_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
